import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import ReactECharts from "echarts-for-react";

import "../../OperazioniHr/OperazioniHr.css";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "../Generale/Generale.css"
import Lf23Loader from "../../../components/Lf23Loader";

const SpazzatoGenerale = ({ filters, loading: parentLoading = false }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // stati per i dati
    const [tableData, setTableData] = useState([]);
    const [barChartData, setBarChartData] = useState([]);
    const [lineChartData, setLineChartData] = useState([]);
    const [lineChartDataFiltered, setLineChartDataFiltered] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);

    // stato per la targa selezionata (filtro dinamico)
    const [selectedTarga, setSelectedTarga] = useState(null);

    // traccia se il click è avvenuto dal grafico (filtra tabella) o dalla tabella (solo evidenzia)
    const [clickSource, setClickSource] = useState(null); // 'chart' o 'table'

    // stato per la vista del grafico a linee: mese-trimestre-anno
    const [chartViewType, setChartViewType] = useState('mese');

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    // stati fullscreen
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);
    const [isFullscreenBar, setIsFullscreenBar] = useState(false);
    const [isFullscreenLine, setIsFullscreenLine] = useState(false);

    // stato per il periodo selezionato dal grafico a linee
    const [selectedPeriod, setSelectedPeriod] = useState(null);

    // dati filtrati per periodo (tabella e grafico a barre)
    const [periodFilteredTableData, setPeriodFilteredTableData] = useState([]);
    const [periodFilteredBarChartData, setPeriodFilteredBarChartData] = useState([]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenTable(false);
                setIsFullscreenBar(false);
                setIsFullscreenLine(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreenTable, isFullscreenBar, isFullscreenLine]);

    // Funzione per formattare i numeri in formato italiano (1.234,56)
    const formatNumber = (value) => {
        const num = parseFloat(value);
        if (isNaN(num)) return '0,00';
        return num.toLocaleString('it-IT', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true
        });
    };

    // Funzione per aggregare i dati del grafico per trimestre o anno
    const aggregateLineChartData = (data, viewType) => {
        // Se la vista è mensile, ritorna i dati così come sono
        if (viewType === 'mese') {
            return data;
        }

        // Oggetto per raggruppare i dati
        const grouped = {};

        // Ciclo su ogni elemento dei dati
        data.forEach(item => {
            // item.mese è nel formato "2024-01"
            const [anno, mese] = item.mese.split('-');

            // Calcolo la chiave di raggruppamento
            let chiave;
            if (viewType === 'trimestre') {
                // Calcolo il trimestre: mesi 1-3 = Q1, 4-6 = Q2, 7-9 = Q3, 10-12 = Q4
                const trimestre = Math.ceil(parseInt(mese) / 3);
                chiave = `Q${trimestre} ${anno}`;
            } else {
                // Vista annuale: raggruppo per anno
                chiave = anno;
            }

            // Se la chiave non esiste, la creo
            if (!grouped[chiave]) {
                grouped[chiave] = {
                    mese: chiave,
                    kmSpazzati: 0,
                    oreSpazzate: 0,
                    // Per spazzatoMtSec serve la somma pesata
                    totaleMetri: 0,
                    totaleSecondi: 0
                };
            }

            // Sommo i valori
            grouped[chiave].kmSpazzati += parseFloat(item.kmSpazzati || 0);
            grouped[chiave].oreSpazzate += parseFloat(item.oreSpazzate || 0);
            // Per calcolare la media pesata di spazzatoMtSec
            grouped[chiave].totaleMetri += parseFloat(item.kmSpazzati || 0) * 1000;
            grouped[chiave].totaleSecondi += parseFloat(item.oreSpazzate || 0) * 3600;
        });

        // Converto l'oggetto in array e calcolo spazzatoMtSec
        return Object.values(grouped).map(item => ({
            mese: item.mese,
            kmSpazzati: item.kmSpazzati.toFixed(2),
            oreSpazzate: item.oreSpazzate.toFixed(2),
            spazzatoMtSec: item.totaleSecondi > 0
                ? (item.totaleMetri / item.totaleSecondi).toFixed(2)
                : '0.00'
        }));
    };


    // Fetch dei dati quando cambiano i filtri
    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true);
            try {
                // Prepara i parametri
                const params = {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    commessa: filters.commessa?.map(c => c.value).join(",") || "",
                    operatore: filters.operatore?.map(o => o.value).join(",") || "",
                    tipologiaVeicolo: filters.tipologiaVeicolo?.map(t => t.value).join(",") || "",
                    targa: filters.targa?.map(t => t.value).join(",") || ""
                };

                const res = await axios.get(`${API_URL}/spazzato/generale`, { params });

                setTableData(res.data.tableData || []);
                setBarChartData(res.data.barChartData || []);
                setLineChartData(res.data.lineChartData || []);
            } catch (error) {
                console.error("Errore nel caricamento dei dati:", error);
            } finally {
                setDataLoading(false);
            }
        };

        fetchData();
        // Reset della targa e periodo selezionati quando cambiano i filtri
        setSelectedTarga(null);
        setClickSource(null);
        setSelectedPeriod(null);
    }, [API_URL, filters]);

    // Fetch dati filtrati per il grafico a linee quando cambia la targa selezionata
    useEffect(() => {
        const fetchFilteredLineData = async () => {
            if (!selectedTarga) {
                setLineChartDataFiltered([]);
                return;
            }

            try {
                const params = {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    targa: selectedTarga
                };

                const res = await axios.get(`${API_URL}/spazzato/generale`, { params });
                setLineChartDataFiltered(res.data.lineChartData || []);
            } catch (error) {
                console.error("Errore nel caricamento dei dati filtrati:", error);
            }
        };

        fetchFilteredLineData();
    }, [API_URL, filters.fromDate, filters.toDate, selectedTarga]);

    // Fetch dati filtrati per periodo quando si clicca sul grafico a linee
    useEffect(() => {
        const fetchPeriodFilteredData = async () => {
            if (!selectedPeriod) {
                setPeriodFilteredTableData([]);
                setPeriodFilteredBarChartData([]);
                return;
            }

            try {
                const { fromDate, toDate } = getPeriodDates(selectedPeriod, chartViewType);

                const params = {
                    fromDate,
                    toDate,
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    commessa: filters.commessa?.map(c => c.value).join(",") || "",
                    operatore: filters.operatore?.map(o => o.value).join(",") || "",
                    tipologiaVeicolo: filters.tipologiaVeicolo?.map(t => t.value).join(",") || "",
                    targa: filters.targa?.map(t => t.value).join(",") || ""
                };

                const res = await axios.get(`${API_URL}/spazzato/generale`, { params });
                setPeriodFilteredTableData(res.data.tableData || []);
                setPeriodFilteredBarChartData(res.data.barChartData || []);
            } catch (error) {
                console.error("Errore nel caricamento dei dati per periodo:", error);
            }
        };

        fetchPeriodFilteredData();
    }, [API_URL, selectedPeriod, chartViewType]);

    // validazione formato dati
    const hasValidDataFormat = tableData !== null && Array.isArray(tableData);

    // combino loading del padre con validazioni locali - stati per il rendering condizi0onale
    const isLoading = parentLoading || dataLoading || !hasValidDataFormat;
    const isDataReady = !parentLoading && hasValidDataFormat && tableData.length > 0;
    const isEmpty = !parentLoading && hasValidDataFormat && tableData.length === 0;

    // Click handler per la tabella (solo evidenzia, non filtra)
    const handleTableRowClick = (targa) => {
        if (selectedTarga === targa) {
            setSelectedTarga(null);
            setClickSource(null);
        } else {
            setSelectedTarga(targa);
            setClickSource('table');
        }
    };

    // Click handler per il grafico a barre (filtra la tabella)
    const handleBarChartClick = (params) => {
        const filteredData = barChartData.filter(d => parseFloat(d.kmSpazzati) > 0);
        const targa = filteredData[params.dataIndex]?.targa;
        if (!targa) return;

        if (selectedTarga === targa) {
            setSelectedTarga(null);
            setClickSource(null);
        } else {
            setSelectedTarga(targa);
            setClickSource('chart');
        }
    };

    // Helper per calcolare le date di inizio e fine di un periodo
    const getPeriodDates = (periodLabel, viewType) => {
        let fromDate, toDate;

        if (viewType === 'mese') {
            // periodLabel è nel formato "2024-01"
            const [year, month] = periodLabel.split('-');
            fromDate = `${year}-${month}-01`;
            const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
            toDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
        } else if (viewType === 'trimestre') {
            // periodLabel è nel formato "Q1 2024"
            const match = periodLabel.match(/Q(\d)\s+(\d{4})/);
            if (match) {
                const quarter = parseInt(match[1]);
                const year = match[2];
                const startMonth = (quarter - 1) * 3 + 1;
                const endMonth = quarter * 3;
                fromDate = `${year}-${String(startMonth).padStart(2, '0')}-01`;
                const lastDay = new Date(parseInt(year), endMonth, 0).getDate();
                toDate = `${year}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
            }
        } else if (viewType === 'anno') {
            // periodLabel è nel formato "2024"
            fromDate = `${periodLabel}-01-01`;
            toDate = `${periodLabel}-12-31`;
        }

        return { fromDate, toDate };
    };

    // Click handler per il grafico a linee (filtra tabella e grafico a barre per periodo)
    const handleLineChartClick = (params) => {
        const periodLabel = activeLineData[params.dataIndex]?.mese;
        if (!periodLabel) return;

        if (selectedPeriod === periodLabel) {
            // Deseleziona
            setSelectedPeriod(null);
            setPeriodFilteredTableData([]);
            setPeriodFilteredBarChartData([]);
        } else {
            // Seleziona il nuovo periodo
            setSelectedPeriod(periodLabel);
            // Reset selezione targa quando si seleziona un periodo
            setSelectedTarga(null);
            setClickSource(null);
        }
    };

    // Usa i dati filtrati per periodo se selezionato, altrimenti i dati originali
    const activeBarChartData = selectedPeriod && periodFilteredBarChartData.length > 0
        ? periodFilteredBarChartData
        : barChartData;

    // Filtra le targhe con km spazzati > 0 per il grafico a barre
    const filteredBarChartData = activeBarChartData.filter(d => parseFloat(d.kmSpazzati) > 0);

    // Configurazione grafico a barre
    const barChartOptions = {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" }
        },
        legend: {
            bottom: 0,
            data: ["Km Spazzati", "Spazzato Mt/Sec"]
        },
        grid: {
            left: 60,
            right: 60,
            bottom: 80,
            top: 50
        },
        xAxis: {
            type: "category",
            data: filteredBarChartData.map(d => d.targa),
            axisLabel: { rotate: 45, fontSize: 10 }
        },
        yAxis: [
            {
                type: "value",
                name: "Km Spazzati",
                position: "left"
            },
            {
                type: "value",
                name: "Mt/Sec",
                position: "right"
            }
        ],
        series: [
            {
                name: "Km Spazzati",
                type: "bar",
                itemStyle: { color: "#4CAF50" },
                data: filteredBarChartData.map(d => ({
                    value: d.kmSpazzati,
                    itemStyle: selectedTarga
                        ? d.targa === selectedTarga
                            ? { opacity: 1, shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.5)' }
                            : { opacity: 0.3 }
                        : {}
                }))
            },
            {
                name: "Spazzato Mt/Sec",
                type: "line",
                yAxisIndex: 1,
                itemStyle: { color: "#000" },
                lineStyle: { type: "dashed", color: selectedTarga ? "rgba(0,0,0,0.3)" : "#000" },
                data: filteredBarChartData.map(d => ({
                    value: d.spazzatoMtSec,
                    itemStyle: selectedTarga
                        ? d.targa === selectedTarga
                            ? { opacity: 1 }
                            : { opacity: 0.3 }
                        : {}
                }))
            }
        ]
    };

    // Dati da usare per il grafico a linee (filtrati o totali)
    const rawLineData = selectedTarga && lineChartDataFiltered.length > 0
        ? lineChartDataFiltered
        : lineChartData;

    // Applica l'aggregazione in base alla vista selezionata (mese/trimestre/anno)
    const activeLineData = aggregateLineChartData(rawLineData, chartViewType);


    // Configurazione grafico a linee
    const lineChartOptions = {
        tooltip: {
            trigger: "axis"
        },
        legend: {
            bottom: 0,
            data: ["Km Spazzati", "Ore Spazzate", "Spazzato Mt al Sec"]
        },
        xAxis: {
            type: "category",
            data: activeLineData.map(d => {
                if (chartViewType === 'mese' && d.mese && d.mese.includes('-')) {
                    const [anno, mese] = d.mese.split('-');
                    const nomiMesi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
                        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
                    return `${nomiMesi[parseInt(mese) - 1]} ${anno}`;
                }
                return d.mese;
            }),
            triggerEvent: true
        },
        yAxis: [
            {
                type: "value",
                name: "Km / Ore",
                position: "left"
            },
            {
                type: "value",
                name: "Mt/Sec",
                position: "right"
            }
        ],
        series: [
            {
                name: "Km Spazzati",
                type: "line",
                data: activeLineData.map(d => ({
                    value: d.kmSpazzati,
                    itemStyle: selectedPeriod
                        ? d.mese === selectedPeriod
                            ? { opacity: 1 }
                            : { opacity: 0.3 }
                        : {}
                })),
                itemStyle: { color: "#0C517C" },
                lineStyle: selectedPeriod ? { opacity: 0.5 } : {},
                symbolSize: activeLineData.map(d => d.mese === selectedPeriod ? 12 : 6)
            },
            {
                name: "Ore Spazzate",
                type: "line",
                data: activeLineData.map(d => ({
                    value: d.oreSpazzate,
                    itemStyle: selectedPeriod
                        ? d.mese === selectedPeriod
                            ? { opacity: 1 }
                            : { opacity: 0.3 }
                        : {}
                })),
                itemStyle: { color: "#C8EA75" },
                lineStyle: selectedPeriod ? { opacity: 0.5 } : {},
                symbolSize: activeLineData.map(d => d.mese === selectedPeriod ? 12 : 6)
            },
            {
                name: "Spazzato Mt al Sec",
                type: "line",
                yAxisIndex: 1,
                data: activeLineData.map(d => ({
                    value: d.spazzatoMtSec,
                    itemStyle: selectedPeriod
                        ? d.mese === selectedPeriod
                            ? { opacity: 1 }
                            : { opacity: 0.3 }
                        : {}
                })),
                lineStyle: { type: "dashed", color: "#000", opacity: selectedPeriod ? 0.5 : 1 },
                itemStyle: { color: "#D64550" },
                symbolSize: activeLineData.map(d => d.mese === selectedPeriod ? 12 : 6)
            }
        ]
    };

    // Usa i dati filtrati per periodo se selezionato, altrimenti i dati originali
    const activeTableData = selectedPeriod && periodFilteredTableData.length > 0
        ? periodFilteredTableData
        : tableData;

    // Filtra i dati della tabella solo se il click è avvenuto dal grafico a barre
    const displayedTableData = (selectedTarga && clickSource === 'chart')
        ? activeTableData.filter(row => row.targa === selectedTarga)
        : activeTableData;

    const sortedDisplayedTableData = useMemo(() => {
        if (!sortConfig.key) return displayedTableData;
        return [...displayedTableData].sort((a, b) => {
            const raw_a = a[sortConfig.key] ?? '';
            const raw_b = b[sortConfig.key] ?? '';
            const num_a = parseFloat(raw_a);
            const num_b = parseFloat(raw_b);
            const cmp = !isNaN(num_a) && !isNaN(num_b)
                ? num_a - num_b
                : String(raw_a).localeCompare(String(raw_b));
            return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
    }, [displayedTableData, sortConfig]);

    // Calcolo dei totali per la riga finale (usa i dati visualizzati)
    const dataForTotals = (selectedTarga && clickSource === 'chart')
        ? displayedTableData
        : activeTableData;
    const sums = dataForTotals.reduce((acc, row) => {
        return {
            distanzaKm: acc.distanzaKm + parseFloat(row.distanzaKm || 0),
            kmSpazzati: acc.kmSpazzati + parseFloat(row.kmSpazzati || 0),
            oreSpazzate: acc.oreSpazzate + parseFloat(row.oreSpazzate || 0)
        };
    }, { distanzaKm: 0, kmSpazzati: 0, oreSpazzate: 0 });

    // Spazzato Mt/Sec totale = (km spazzati in metri) / (ore spazzate in secondi)
    const totals = {
        ...sums,
        spazzatoMtSec: sums.oreSpazzate > 0
            ? (sums.kmSpazzati * 1000) / (sums.oreSpazzate * 3600)
            : 0
    };

    return (
        <div className="spazzato-generale-container">
            {isLoading ? (
                <Lf23Loader />
            ) : isEmpty ? (
                <div className="oper-hr-empty-state">
                    <div className="oper-hr-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                        </svg>
                    </div>
                    <h4 className="oper-hr-empty-title">Nessun dato disponibile</h4>
                    <p className="oper-hr-empty-text">
                        Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.
                    </p>
                </div>
            ) : isDataReady ? (
                <>
                    <div className="spazzato-generale-row">
                        {/* Tabella a sinistra */}
                        <div className={`spazzato-generale-table-section${isFullscreenTable ? ' fullscreen-chart' : ''}`}>
                            <RotateDevice active={isFullscreenTable} />
                            <div className="spazzato-generale-table-header">
                                <span className="spazzato-generale-table-title">
                                    {selectedPeriod
                                        ? `Dettaglio per Targa - ${selectedPeriod}`
                                        : "Dettaglio per Targa"
                                    }
                                </span>
                                <button
                                    className="spazzato-generale-fullscreen-btn"
                                    onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                                    title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenTable ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <div className="spazzato-generale-table-wrapper">
                                <table className="spazzato-table">
                                    <thead>
                                        <tr>
                                            {[
                                                { label: 'Targa', key: 'targa' },
                                                { label: 'Distanza Km', key: 'distanzaKm' },
                                                { label: 'Km Spazzati', key: 'kmSpazzati' },
                                                { label: 'Ore Spazzate', key: 'oreSpazzate' },
                                                { label: 'Spazzato Mt/Sec', key: 'spazzatoMtSec' },
                                            ].map(({ label, key }) => (
                                                <th key={key} className="spazzato-th-sortable" onClick={() => handleSort(key)}>
                                                    {label}
                                                    <span className="spazzato-sort-icon">
                                                        {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedDisplayedTableData.length > 0 ? (
                                            sortedDisplayedTableData.map((row, index) => (
                                                <tr
                                                    key={index}
                                                    onClick={() => handleTableRowClick(row.targa)}
                                                    className={`spazzato-table-row-clickable ${clickSource === 'table' && selectedTarga === row.targa ? 'spazzato-table-row-selected' : ''} ${clickSource === 'table' && selectedTarga && selectedTarga !== row.targa ? 'spazzato-table-row-dimmed' : ''}`}
                                                >
                                                    <td>{row.targa}</td>
                                                    <td>{formatNumber(row.distanzaKm)}</td>
                                                    <td>{formatNumber(row.kmSpazzati)}</td>
                                                    <td>{formatNumber(row.oreSpazzate)}</td>
                                                    <td>{formatNumber(row.spazzatoMtSec)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" style={{ textAlign: "center" }}>
                                                    Nessun dato disponibile
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr className="spazzato-table-total-row">
                                            <td><strong>TOTALE</strong></td>
                                            <td><strong>{formatNumber(totals.distanzaKm)}</strong></td>
                                            <td><strong>{formatNumber(totals.kmSpazzati)}</strong></td>
                                            <td><strong>{formatNumber(totals.oreSpazzate)}</strong></td>
                                            <td><strong>{formatNumber(totals.spazzatoMtSec)}</strong></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>


                        {/* Grafico a barre a destra */}
                        <div className={`spazzato-generale-chart-container${isFullscreenBar ? ' fullscreen-chart' : ''}`}>
                            <RotateDevice active={isFullscreenBar} />
                            <div className="spazzato-generale-table-header">
                                <span className="spazzato-generale-table-title">
                                    {selectedPeriod
                                        ? `Km Spazzati e Spazzato al secondo per Targa - ${selectedPeriod}`
                                        : "Km Spazzati e Spazzato al secondo per Targa"
                                    }
                                </span>
                                <button
                                    className="spazzato-generale-fullscreen-btn"
                                    onClick={() => setIsFullscreenBar(!isFullscreenBar)}
                                    title={isFullscreenBar ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenBar ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <ReactECharts
                                option={barChartOptions}
                                style={{ height: "350px", width: "100%" }}
                                onEvents={{ click: handleBarChartClick }}
                            />
                        </div>
                    </div>

                    {/* Grafico a linee sotto */}
                    <div className={`spazzato-generale-line-chart${isFullscreenLine ? ' fullscreen-chart' : ''}`}>
                        <RotateDevice active={isFullscreenLine} />
                        {/* Titolo e bottoni per cambiare vista */}
                        <div className="oper-hr-table-title-container" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <span className="spazzato-generale-table-title charts-title">
                                {selectedTarga
                                    ? `Totale Km spazzati per ${chartViewType} - ${selectedTarga}`
                                    : `Totale Km spazzati per ${chartViewType}`
                                }
                            </span>
                            <div className="oper-hr-table-view-type-container">
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${chartViewType === 'mese' ? 'active' : ''}`}
                                    onClick={() => { setChartViewType('mese'); setSelectedPeriod(null); }}
                                    title="Vista Mensile"
                                >
                                    <span>M</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${chartViewType === 'trimestre' ? 'active' : ''}`}
                                    onClick={() => { setChartViewType('trimestre'); setSelectedPeriod(null); }}
                                    title="Vista Trimestrale"
                                >
                                    <span>T</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${chartViewType === 'anno' ? 'active' : ''}`}
                                    onClick={() => { setChartViewType('anno'); setSelectedPeriod(null); }}
                                    title="Vista Annuale"
                                >
                                    <span>A</span>
                                </button>
                                <button
                                    className="spazzato-generale-fullscreen-btn"
                                    onClick={() => setIsFullscreenLine(!isFullscreenLine)}
                                    title={isFullscreenLine ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenLine ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <ReactECharts
                            option={lineChartOptions}
                            style={{ height: "350px", width: "100%" }}
                            onEvents={{ click: handleLineChartClick }}
                        />
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default SpazzatoGenerale;